import dayjs from 'dayjs';
import { TFunction } from 'i18next';

import { Timespan } from 'types';

export const convertSecondsToTime: (seconds: number) => string = seconds => {
  const days = Math.floor(seconds / (3600 * 24));
  seconds %= 3600 * 24;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);

  return `${days}d ${hours}h ${minutes}m`;
};

/**
 * Formats a duration in seconds for display, keeping the two largest non-zero units.
 * Sub-minute durations keep sub-second precision (`17.54s`), longer ones are rounded
 * to whole seconds (`19m 38s`, `1h 05m`, `2d 03h`). Unlike `convertSecondsToTime`,
 * which always prints days/hours/minutes, this never collapses short spans to `0d 0h 0m`.
 */
export const formatSeconds: (seconds: number) => string = seconds => {
  if (!Number.isFinite(seconds) || seconds < 0) return '-';
  // Compare on the rounded value so 59.999 becomes `1m 00s` rather than `60.00s`.
  if (Math.round(seconds * 100) / 100 < 60) return `${seconds.toFixed(2)}s`;

  // Round before splitting so a remainder of 59.6s cannot render as `60s`.
  const total = Math.round(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const pad = (value: number) => value.toString().padStart(2, '0');

  if (days > 0) return `${days}d ${pad(hours)}h`;
  if (hours > 0) return `${hours}h ${pad(minutes)}m`;

  return `${minutes}m ${pad(total % 60)}s`;
};

export const getSupportedLanguage: (i18nLanguage: string) => string = i18nLanguage => {
  if (i18nLanguage.includes('en')) return 'en';
  if (i18nLanguage.includes('cs')) return 'cs';
  if (i18nLanguage.includes('de')) return 'de';

  return 'en';
};

export const getCountryCode = (language: string) => {
  const parsedLanguage = language.includes('-') ? language.split('-')[0].toLowerCase() : language.toLowerCase();

  if (parsedLanguage === 'en') return 'gb';
  if (parsedLanguage === 'cs') return 'cz';
  if (parsedLanguage === 'de') return 'de';
  return 'gb';
};

export const getCountryLanguage = (countryIso3: string) => {
  switch (countryIso3.toLowerCase()) {
    case 'cze':
      return 'cs';
    case 'deu':
      return 'de';
    case 'aut':
      return 'de';
    case 'svk':
      return 'en';
    default:
      return 'en';
  }
};

export const parseTimespan = (timespan: Timespan, endDate: dayjs.Dayjs = dayjs(), includeToday = false) => {
  let startDate = endDate.clone();

  switch (timespan) {
    case 'last-week':
      startDate = startDate.add(-7, 'day');
      break;
    case 'last-month':
      startDate = startDate.add(-1, 'month');
      break;
    case 'last-quarter':
      startDate = startDate.add(-3, 'month');
      break;
    case 'last-year':
      startDate = startDate.add(-1, 'year');
      break;
    case 'this-year':
      startDate = startDate.startOf('year');
      break;
    default:
      startDate = startDate.add(-1, 'month');
      break;
  }

  return {
    start_date: startDate.format('YYYY-MM-DD'),
    end_date: endDate.add(includeToday ? 1 : 0, 'day').format('YYYY-MM-DD')
  };
};

export const setDayJsLocale = async (locale: string) => {
  const code = (locale.split('-')[0] || 'en').toLowerCase();

  if (code === 'cs') {
    await import('dayjs/locale/cs');
  } else if (code === 'de') {
    await import('dayjs/locale/de');
  }

  dayjs.locale(code);
};

/**
 * Whether the browser renders clock times with a 12-hour cycle (AM/PM).
 *
 * The native `<input type="time">` used inside the design-system `DateTimePicker` follows the
 * browser's own locale, so any label showing the picked time has to follow the same setting -
 * otherwise the popover says "12:56 PM" while the field above it says "12:56".
 */
export const prefersTwelveHourClock = () => {
  const { hour12, hourCycle } = Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions();

  if (typeof hour12 === 'boolean') return hour12;

  return hourCycle === 'h11' || hourCycle === 'h12';
};

/** Time format token respecting the browser's 12/24-hour setting, e.g. `14:56` or `02:56 PM`. */
export const getTimeFormat = () => (prefersTwelveHourClock() ? 'hh:mm A' : 'HH:mm');

/**
 * Formats a date and its clock time, with the time part following the browser's 12/24-hour setting.
 * The date part stays in the app-wide `DD.MM.YYYY` order unless overridden.
 */
export const formatDateTime = (date: dayjs.ConfigType, dateFormat = 'DD.MM.YYYY') =>
  dayjs(date).format(`${dateFormat} (${getTimeFormat()})`);

export const dateBetween = (date: dayjs.ConfigType, startDate: dayjs.ConfigType, endDate: dayjs.ConfigType) => {
  if (!startDate || !endDate) return true;
  if (!date) return false;

  return dayjs(date).isBetween(startDate, endDate, 'day', '[]');
};

export const isDstTransitionDay = (localDay: Date): boolean => {
  const dayStart = new Date(localDay.getFullYear(), localDay.getMonth(), localDay.getDate(), 0, 0, 0);
  const offsets = new Set<number>();

  for (const h of [0, 1, 2, 3, 4, 6, 12, 18, 23]) {
    const sampleTime = new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate(), h, 0, 0);
    offsets.add(sampleTime.getTimezoneOffset());
  }
  return offsets.size > 1;
};

export const getLocalizedWeekdays = () => {
  const weekdays = dayjs.weekdaysShort(false);

  return weekdays.slice(1).concat(weekdays[0]);
};

export const localizeTimeString = (
  amount: number,
  unit: 'day' | 'week' | 'month' | 'year',
  past: boolean,
  t: TFunction
) => {
  if (unit === 'day') {
    if (amount >= 5) return past ? t('time.pastDay.5') : t('time.day.5');
    if (amount >= 2) return past ? t('time.pastDay.2') : t('time.day.2');
    if (amount == 1) return past ? t('time.pastDay.1') : t('time.day.1');

    return past ? t('time.pastDay.1') : t('time.day.1');
  }

  if (unit === 'week') {
    if (amount >= 5) return past ? t('time.pastWeek.5') : t('time.week.5');
    if (amount >= 2) return past ? t('time.pastWeek.2') : t('time.week.2');
    if (amount == 1) return past ? t('time.pastWeek.1') : t('time.week.1');

    return past ? t('time.pastWeek.1') : t('time.week.1');
  }

  if (unit === 'month') {
    if (amount >= 5) return past ? t('time.pastMonth.5') : t('time.month.5');
    if (amount >= 2) return past ? t('time.pastMonth.2') : t('time.month.2');
    if (amount == 1) return past ? t('time.pastMonth.1') : t('time.month.1');

    return past ? t('time.pastMonth.1') : t('time.month.1');
  }

  if (unit === 'year') {
    if (amount >= 5) return past ? t('time.pastYear.5') : t('time.year.5');
    if (amount >= 2) return past ? t('time.pastYear.2') : t('time.year.2');
    if (amount == 1) return past ? t('time.pastYear.1') : t('time.year.1');

    return past ? t('time.pastYear.1') : t('time.year.1');
  }

  return '?';
};
